import { useState } from 'react';
import { FormProvider, type SubmitHandler, useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { Link, Navigate, To } from 'react-router-dom';
import { zodResolver } from '@hookform/resolvers/zod';
import { DEFAULT_CONFIG } from '@qapline/config';
import { handleErrorsWithNoInputs, useDocumentTitle, useQuery } from '@qapline/helpers';
import { Button, TextField, Typography } from '@qapline/ui';
import jwt_decode from 'jwt-decode';

import { useLoginUserMutation } from '../../api';
import { useAppGuard, useAuth } from '../../hooks';
import { useRefreshToken } from '../../hooks/useRefreshToken';
import { loginSchema, TLoginSchema } from '../../schemas';
import { EAuthRole, TUserAuthTokenPayload } from '../../types';
import { AuthPage } from '../AuthPage';

import styles from './LoginForm.module.scss';

export interface ILoginFormProps {
    forgotPasswordLink?: To;
    registerLink?: To;
    /**
     * Roles permitted to log into this app. A user with any other role is logged
     * back out and notified. Omit to allow any role.
     */
    allowedRoles?: EAuthRole[];
}

export const LoginForm = ({ forgotPasswordLink, registerLink, allowedRoles }: ILoginFormProps) => {
    const { t, i18n } = useTranslation('common', { keyPrefix: 'components.loginForm' });

    useDocumentTitle(t('title'));

    const query = useQuery();
    const { isAuthenticated, user } = useAuth();
    const [loginUser, { isLoading: isLoadingLogin }] = useLoginUserMutation();
    const [visualInputsList, setVisualInputsList] = useState<string[]>([]);
    const { isLoading: isRefreshingToken } = useRefreshToken();
    const isRoleAllowed = useAppGuard(allowedRoles);

    const methods = useForm<TLoginSchema>({
        resolver: zodResolver(loginSchema(i18n)),
        defaultValues: {
            email: query.get('email') || '',
            password: '',
        },
    });
    const {
        handleSubmit,
        formState: { isSubmitting },
        setError,
    } = methods;

    const onSubmit: SubmitHandler<TLoginSchema> = async (credentials) => {
        try {
            const { accessToken } = await loginUser({ ...credentials }).unwrap();

            // Suppress the success toast when the role is not permitted in this app:
            // useAppGuard reacts to the new credentials and handles logout + notification.
            const { role } = jwt_decode<TUserAuthTokenPayload>(accessToken);
            if (allowedRoles && !allowedRoles.includes(role)) {
                return;
            }

            return toast.success(t('response.success'));
        } catch (e: any) {
            toast.error(t('response.error'));
            setError('email', { message: e.message });
        }
    };

    if (isRefreshingToken || isAuthenticated === null) {
        return <AuthPage isLoading />;
    }

    // Authenticated with a role this app does not permit: useAppGuard is logging the
    // user out. Show loading instead of bouncing to "/" until isAuthenticated clears.
    if (isAuthenticated === true && !isRoleAllowed) {
        return <AuthPage isLoading />;
    }

    const enquiryId = query.get('enquiryId');
    if (isAuthenticated === true && enquiryId) {
        return <Navigate to={DEFAULT_CONFIG.routes.offers.detail.replace(':id', enquiryId)} replace />;
    }

    if (isAuthenticated === true && user && enquiryId === null) {
        return <Navigate to="/" replace />;
    }

    return (
        <AuthPage>
            <FormProvider {...methods}>
                <form onSubmit={handleSubmit(onSubmit, (error) => handleErrorsWithNoInputs(error, visualInputsList))}>
                    <Typography variant="h2">{t('title')}</Typography>
                    <div className={styles.fields}>
                        <TextField
                            name="email"
                            type="email"
                            label={t('email')}
                            placeholder={t('email')}
                            autoComplete="username"
                        />
                        <TextField
                            name="password"
                            type="password"
                            label={t('password')}
                            placeholder={t('password')}
                            autoComplete="current-password"
                            labelAction={
                                forgotPasswordLink && (
                                    <Link to={forgotPasswordLink} className={styles.link}>
                                        {t('forgotPassword')}
                                    </Link>
                                )
                            }
                        />
                    </div>
                    {registerLink && (
                        <div className={styles.links}>
                            <Typography color="secondary">
                                {t('register.text')}{' '}
                                <Link to={registerLink} className={styles.link}>{`${t('register.action')}`}</Link>
                            </Typography>
                        </div>
                    )}
                    <div className={styles.buttons}>
                        <Button type="submit" disabled={isSubmitting} loading={isLoadingLogin}>
                            {t('login')}
                        </Button>
                    </div>
                </form>
            </FormProvider>
        </AuthPage>
    );
};
